import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { CUSTOM_ICONS } from "@/constants/Unicode";
import { getNSTMFatigueDocDefinition } from "@/reports/NSTMDetailsTable";
import { steelTypeDisplayValue, getMaxMinSpanLength } from "@/composables/spanBySpan";
import { useSuperstructureStore } from "@/components/inspection/store/superStructure";

function setSuperstructureDocDefinition(structure) {
  const configStore = useConfigStore();
  const superStructureStore = useSuperstructureStore();
  const filteredStructureUnits = superStructureStore.getStructureUnits(structure);
  const totalNumberOfSpans = superStructureStore.getTotalNumberOfSpans(structure);
  let docDefinition = {
    header: function () {
      return getReportHeader("Superstructure Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Superstructure Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Superstructure Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        columns: [
          {
            width: "40%",
            text: "B.C.02 Superstructure Condition Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.supRating),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Overall Superstructure Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.SUPERSTRUCTURE,
        )?.notes,
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.01 Combustible Material Under Bridge: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.MATERIAL_STORED_UNDER,
              structure?.userInspection?.matStoreUnd,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Overall Combustible Material Under Bridge Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.COMBUSTIBLE_MATERIALS_COMMENTS,
        )?.notes,
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.02 New Coating Since Last Inspection: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.newPaint === "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
            style: "customFontAwesome",
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.03 Protective Coating Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING,
              structure?.userInspection?.paintCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "40%",
            text: "SUP.04 Protective Coating (Extent) Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.PAINT_CONDITION_RATING_EXTENT,
              structure?.userInspection?.paintExtentRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Overall Protective Coating Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.PAINT_OVERALL,
        )?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Superstructure Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "30%",
            text: "G.01 Total Number of Spans: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: totalNumberOfSpans,
            margin: [5, 5, 0, 0],
          },
          {
            width: "27%",
            text: "B.G.11 Skew: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: (structure?.structureBridge?.skew || "") + "°",
            margin: [5, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.01 NBIS Bridge Length: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: structure?.structureBridge?.nbisBridgeLength,
            margin: [5, 5, 0, 0],
          },
          {
            width: "27%",
            text: "B.G.12 Curved Bridge: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CURVED_BRIDGE, structure.structureBridge.curvedBridge),
            margin: [5, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.03 Maximum Span Length: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: getMaxMinSpanLength(filteredStructureUnits, ["M", "A", "W"])?.maxLength,
            margin: [5, 5, 0, 0],
          },
          {
            width: "27%",
            text: "D.08 Scuppers w/Downspouts: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.scuppersWithDownspouts || 0,
            margin: [5, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.04 Minimum Span Length: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: getMaxMinSpanLength(filteredStructureUnits, ["M", "A", "W"])?.minLength,
            margin: [5, 5, 0, 0],
          },
          {
            width: "27%",
            text: "D.09 Scuppers w/Downspouts: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.scuppersWithoutDownspouts || 0,
            margin: [5, 5, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "D.06 # of Bridge Cleaning Locations: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "20%",
            text: structure?.userBridge?.seatCleaning,
            margin: [5, 5, 0, 0],
          },
        ],
      },
      {
        width: "30%",
        text: "D.07 Bridge Seat Cleaning Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.userBridge?.seatCleaningNote,
      },
      ...getSpanBySpanContent(structure, filteredStructureUnits),
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

const getSpanHeader = (structure, structureUnit) => {
  const configStore = useConfigStore();
  return {
    table: {
      headerRows: 0,
      widths: ["30%", "*", "30%"],
      body: [
        [
          {
            text: `Structure Unit ID: ${structureUnit.strUnitLabel}`,
            fillColor: "#ebebeb",
            alignment: "center",
            style: ["sectionHeader"],
          },
          {
            text: `Span Type: ${configStore.getReferenceValue(
              REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
              structureUnit.legacyStructureType,
            )}`,
            fillColor: "#ebebeb",
            alignment: "center",
            style: ["sectionHeader"],
          },
          {
            text: `Unit Key: ${structureUnit.strUnitKey}`,
            fillColor: "#ebebeb",
            alignment: "center",
            style: ["sectionHeader"],
          },
        ],
      ],
    },
    layout: "noBorders",
    margin: [0, 5, 0, 0],
  };
};

const getSpanDetails = (structureUnit) => {
  const configStore = useConfigStore();

  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: `Span ${structureUnit.strUnitLabel} Details`,
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    {
      columns: [
        {
          width: "30%",
          text: "SU.01 Unit Key: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: structureUnit.strUnitKey,
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SU.02 Unit ID: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: structureUnit.strUnitLabel,
          margin: [5, 5, 0, 0],
        },
      ],
    },

    {
      width: "30%",
      text: "SU.03 Structure Unit Description: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnit.strUnitDescription,
    },
    {
      columns: [
        {
          width: "30%",
          text: "SU.04 Legacy Structure Type: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_TYPE, structureUnit.legacyStructureType),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.21 Span Length: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: structureUnit.spanLength,
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "SP.02 Main Span Set Indicator: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(
            REFERENCE_TABLE.MAIN_SPANSET_INDICATOR,
            structureUnit.mainSpanSetIndicator,
          ),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.22 Span Flared: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: structureUnit.spanFlared === "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
          style: "customFontAwesome",
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.01 Span Config. Designation: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: structureUnit.spanConfigDesignation,
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.03 NSTM Group: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.FC_GROUP_NUMBER, structureUnit.nstmGroupNumber),
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.03 Number of Beam Lines: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: structureUnit.numberOfBeamLines,
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.05 Type of Steel: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: steelTypeDisplayValue(structureUnit?.structureUnitSteelTypes?.map((s) => s.steelType)),
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.04 Span Material: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_MATERIAL, structureUnit.spanMaterial),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.24 Dept Material Type: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.MATERIAL_MAKEUP, structureUnit.deptMaterialType),
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.05 Span Continuity: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_CONTINUITY, structureUnit.spanContinuity),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.25 Dept Physical Type: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.PHYSICAL_MAKEUP, structureUnit.deptPhysicalType),
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.06 Span Type: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_TYPE, structureUnit.spanType),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.26 Dept Span Inter: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_INTERACTION, structureUnit.deptSpanInteraction),
          margin: [5, 5, 0, 0],
        },
      ],
    },
    {
      columns: [
        {
          width: "30%",
          text: "B.SP.07 Span Prot. System: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "25%",
          text: configStore.getReferenceValue(REFERENCE_TABLE.SPAN_PROTECTION_SYSTEM, structureUnit.spanProtectSystem),
          margin: [5, 5, 0, 0],
        },
        {
          width: "25%",
          text: "SP.27 Dept Struc Config: ",
          bold: true,
          margin: [0, 5, 0, 0],
        },
        {
          width: "*",
          text: configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURAL_CONFIGURATION, structureUnit.deptStrucConfig),
          margin: [5, 5, 0, 0],
        },
      ],
    },
  ];
};

const getSpanSuperStructureNotes = (structureUnitInspComments, strUnitLabel) => {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: `Span ${strUnitLabel} Superstructure Notes`,
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    {
      text: "Girders/Beams: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_GIRDERS)
        ?.notes,
    },
    {
      text: "Floorbeams: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_FLOORBEAMS)
        ?.notes,
    },
    {
      text: "Stringers: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_STRINGERS)
        ?.notes,
    },
    {
      text: "Diaphragms: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_DIAPHRAGMS)
        ?.notes,
    },
    {
      text: "Truss Members: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_TRUSS_MEMBERS)
        ?.notes,
    },
    {
      text: "Portals/Bracings: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_PORTALS)
        ?.notes,
    },
    {
      text: "Drainage System: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.SUPER_DRAINAGE)
        ?.notes,
    },
    {
      text: "Combustible Materials Under Bridge: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find(
        (element) => element?.commentType == INSPECTION_COMMENTS.COMBUSTIBLE_MATERIALS_UNDER_BRIDGE,
      )?.notes,
    },
  ];
};

const getSpanProtectiveCoatingNotes = (structureUnitInspComments, strUnitLabel) => {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: `Span ${strUnitLabel} Protective Coating Notes`,
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
    {
      text: "Interior Girder/Beam: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find(
        (element) => element?.commentType == INSPECTION_COMMENTS.INTERIOR_BEAM_GIRDER,
      )?.notes,
    },
    {
      text: "Fascia: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.PAINT_FASCIAS)
        ?.notes,
    },
    {
      text: "Splash Zone - Truss/Girder: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.PAINT_SPLASH_ZONE)
        ?.notes,
    },
    {
      text: "Truss: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.PAINT_TRUSS)?.notes,
    },
    {
      text: "Bearing: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.PAINT_BEARINGS)
        ?.notes,
    },
    {
      text: "Other: ",
      bold: true,
      margin: [0, 5, 0, 0],
    },
    {
      text: structureUnitInspComments.find((element) => element?.commentType == INSPECTION_COMMENTS.PAINT_OTHER)?.notes,
    },
  ];
};

const getSpanNSTMDetails = (strUnitLabel) => {
  return [
    {
      table: {
        headerRows: 0,
        widths: ["100%"],
        body: [
          [
            {
              text: `Span ${strUnitLabel} NSTM Details`,
              fillColor: "#ebebeb",
              alignment: "center",
              style: ["sectionHeader"],
            },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, 5, 0, 0],
    },
  ];
};

const getSpanBySpanContent = (structure, filteredStructureUnits) => {
  const spanBySpanContent = [];

  if (filteredStructureUnits.length === 0) {
    spanBySpanContent.push({
      text: "This structure currently contains no structure unit details.",
      bold: true,
      alignment: "center",
      margin: [0, 10, 0, 0],
    });
    return spanBySpanContent;
  }

  for (const structureUnit of filteredStructureUnits ?? []) {
    const filterStructrueUnitInspComments = structure.structureUnitInspComments.filter(
      (element) => element?.structureUnitId === structureUnit.structureUnitId,
    );
    spanBySpanContent.push(
      getSpanHeader(structure, structureUnit),
      ...getSpanDetails(structureUnit),
      ...getSpanSuperStructureNotes(filterStructrueUnitInspComments, structureUnit.strUnitLabel),
      ...getSpanProtectiveCoatingNotes(filterStructrueUnitInspComments, structureUnit.strUnitLabel),
      ...getSpanNSTMDetails(structureUnit.strUnitLabel),
      getNSTMFatigueDocDefinition(structureUnit.strUnitKey),
    );
  }
  return spanBySpanContent;
};

export { setSuperstructureDocDefinition };
